import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';

export interface Macro {
  id: string;
  org_id: string;
  user_id: string | null;
  title: string;
  content: string;
  is_global: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateMacroInput {
  title: string;
  content: string;
  is_global: boolean;
}

export interface UpdateMacroInput {
  id: string;
  title?: string;
  content?: string;
}

export function useMacros() {
  const { user } = useAuth();
  const { currentOrg } = useOrganizationContext();
  const queryClient = useQueryClient();

  // Fetch all macros (global + individual)
  const { data: macros = [], isLoading, refetch } = useQuery({
    queryKey: ['macros', currentOrg?.id, user?.id],
    queryFn: async () => {
      if (!currentOrg?.id || !user?.id) return [];

      const { data, error } = await supabase
        .from('macros')
        .select('*')
        .eq('org_id', currentOrg.id)
        .or(`is_global.eq.true,user_id.eq.${user.id}`)
        .order('title', { ascending: true });

      if (error) throw error;
      return data as Macro[];
    },
    enabled: !!currentOrg?.id && !!user?.id,
  });

  // Separate global and individual macros
  const globalMacros = macros.filter((m) => m.is_global);
  const individualMacros = macros.filter((m) => !m.is_global);

  // Create macro
  const createMacro = useMutation({
    mutationFn: async (input: CreateMacroInput) => {
      if (!currentOrg?.id || !user?.id) {
        throw new Error('Organização ou usuário não encontrado');
      }

      const macroData = {
        org_id: currentOrg.id,
        user_id: input.is_global ? null : user.id,
        title: input.title.trim(),
        content: input.content.trim(),
        is_global: input.is_global,
      };

      const { data, error } = await supabase
        .from('macros')
        .insert(macroData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['macros'] });
      toast.success('Macro criada com sucesso!');
    },
    onError: (error: any) => {
      console.error('Erro ao criar macro:', error);
      toast.error(error.message || 'Erro ao criar macro');
    },
  });

  // Update macro
  const updateMacro = useMutation({
    mutationFn: async (input: UpdateMacroInput) => {
      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      if (input.title !== undefined) {
        updateData.title = input.title.trim();
      }
      if (input.content !== undefined) {
        updateData.content = input.content.trim();
      }

      const { data, error } = await supabase
        .from('macros')
        .update(updateData)
        .eq('id', input.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['macros'] });
      toast.success('Macro atualizada com sucesso!');
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar macro:', error);
      toast.error(error.message || 'Erro ao atualizar macro');
    },
  });

  // Delete macro
  const deleteMacro = useMutation({
    mutationFn: async (macroId: string) => {
      const { error } = await supabase
        .from('macros')
        .delete()
        .eq('id', macroId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['macros'] });
      toast.success('Macro excluída com sucesso!');
    },
    onError: (error: any) => {
      console.error('Erro ao excluir macro:', error);
      toast.error(error.message || 'Erro ao excluir macro');
    },
  });

  // Replace variables in macro content
  const replaceMacroVariables = (
    content: string,
    variables: Record<string, string>
  ): string => {
    let result = content;

    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      result = result.replace(regex, value || '');
    });

    return result;
  };

  return {
    macros,
    globalMacros,
    individualMacros,
    isLoading,
    refetch,
    createMacro: createMacro.mutate,
    updateMacro: updateMacro.mutate,
    deleteMacro: deleteMacro.mutate,
    isCreating: createMacro.isPending,
    isUpdating: updateMacro.isPending,
    isDeleting: deleteMacro.isPending,
    replaceMacroVariables,
  };
}
